import {
  ROUTE_CATEGORIES,
  ROUTE_CATEGORY_LABELS,
  ROUTE_MODE_LABELS,
  ROUTE_STATUS_LABELS,
  ROUTE_STATUSES,
  subject,
  type RouteDetail,
  type RouteListItem,
  type RoutePage,
} from '@ftthnms/shared';
import { Badge, Button, EmptyState, RegionChip } from '@ftthnms/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ChevronLeft,
  ChevronRight,
  Crosshair,
  RotateCcw,
  Route as RouteIcon,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useState, type MouseEvent } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ExportMenu } from '../components/ExportMenu';
import { RouteDrawer, ROUTE_STATUS_TONE } from '../components/RouteDrawer';
import { RouteForm } from '../components/RouteForm';
import { useToast } from '../components/Toast';
import { AssetGlyph } from '../map/AssetGlyph';
import { api, dateTime, metres, nf, qs, send } from '../lib/api';
import { invalidateAssets } from '../lib/assets';
import { useSession } from '../lib/session';

type Panel = { kind: 'view'; id: string } | { kind: 'edit'; route: RouteDetail };

/** Cable route list (RTE-08): filters, total length, bulk delete, trash, focus on the map. */
export function RoutesPage() {
  const { area, ability } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [trash, setTrash] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [panel, setPanel] = useState<Panel | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [withPoles, setWithPoles] = useState(true);
  const pageSize = 25;
  const canDeleteAny = ability.can('delete', 'Route');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [debouncedQ, status, category, area, trash]);

  const list = useQuery({
    queryKey: ['routes', { area, q: debouncedQ, status, category, trash, page }],
    queryFn: () =>
      api<RoutePage>(`/api/v1/routes${qs({ area, q: debouncedQ, status, category, trash, page, pageSize })}`),
    placeholderData: keepPreviousData,
  });
  const data = list.data;
  const rows = data?.items ?? [];
  const selectable = canDeleteAny && !trash;
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const lastPage = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));
  const filtered = !!(debouncedQ || status || category);

  const toggleRow = (id: string, on: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  const stop = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  async function restore(r: RouteListItem) {
    try {
      await send('POST', `/api/v1/routes/${r.id}/restore`);
      await invalidateAssets(qc);
      toast({ tone: 'success', text: 'Jalur dipulihkan.' });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    }
  }

  return (
    <div className="fn-page-body">
      <section className="fn-card" style={{ overflow: 'hidden' }}>
        <div className="fn-toolbar">
          <div className="fn-input-group" style={{ flex: 1, maxWidth: 320, minWidth: 180 }}>
            <Search className="i" aria-hidden />
            <input
              className="fn-input"
              aria-label="Cari jalur"
              placeholder="Cari nama, aset asal/tujuan…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            className="fn-input"
            aria-label="Status jalur"
            style={{ width: 'auto' }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Semua status</option>
            {ROUTE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ROUTE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            className="fn-input"
            aria-label="Kategori"
            style={{ width: 'auto' }}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Semua kategori</option>
            {ROUTE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {ROUTE_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
          {canDeleteAny && (
            <div className="fn-segmented" role="group" aria-label="Tampilan">
              <button
                type="button"
                className="fn-segmented__item"
                aria-pressed={!trash}
                onClick={() => setTrash(false)}
              >
                Aktif
              </button>
              <button
                type="button"
                className="fn-segmented__item"
                aria-pressed={trash}
                onClick={() => setTrash(true)}
              >
                <Trash2 className="i i--sm" aria-hidden /> Tempat sampah
              </button>
            </div>
          )}
          <span className="fn-toolbar__spacer" />
          {list.isFetching && <span className="fn-spinner" aria-label="Memuat" />}
          {!trash && ability.can('export', 'Route') && (
            <ExportMenu entity="routes" params={{ area, routeStatus: status, category }} />
          )}
          {!trash && ability.can('create', 'Route') && (
            <Button
              size="sm"
              variant="primary"
              icon={<RouteIcon className="i" aria-hidden />}
              onClick={() => void navigate({ to: '/map' })}
              title="Jalur digambar di peta"
            >
              Buat di Peta
            </Button>
          )}
        </div>

        {selectable && selected.size > 0 && (
          <div className="fn-bulkbar">
            <span>{nf.format(selected.size)} jalur dipilih</span>
            <span className="fn-toolbar__spacer" />
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Batal pilih
            </Button>
            <Button
              size="sm"
              variant="danger"
              icon={<Trash2 className="i" aria-hidden />}
              onClick={() => setBulkOpen(true)}
            >
              Hapus
            </Button>
          </div>
        )}

        {list.error ? (
          <div style={{ padding: 20 }}>
            <div className="fn-alert fn-alert--danger" role="alert">
              <div className="fn-alert__body">{list.error.message}</div>
            </div>
          </div>
        ) : data && data.total === 0 ? (
          <div style={{ padding: 20 }}>
            <EmptyState
              icon={<RouteIcon className="i i--xl" aria-hidden />}
              title={
                trash
                  ? 'Tempat sampah kosong'
                  : filtered
                    ? 'Tidak ada jalur yang cocok'
                    : 'Belum ada jalur kabel'
              }
              text={
                trash
                  ? 'Jalur yang dihapus bisa dipulihkan di sini selama 30 hari.'
                  : filtered
                    ? 'Hapus filter atau cari dengan kata lain.'
                    : 'Buat jalur di Peta: tombol Buat Jalur, lalu klik aset asal dan tujuan.'
              }
            />
          </div>
        ) : (
          <div className="fn-table-wrap">
            <table className="fn-table">
              <thead>
                <tr>
                  {selectable && (
                    <th className="is-check">
                      <input
                        type="checkbox"
                        aria-label="Pilih semua di halaman ini"
                        checked={allOnPage}
                        onChange={(e) => rows.forEach((r) => toggleRow(r.id, e.target.checked))}
                      />
                    </th>
                  )}
                  <th>Jalur</th>
                  <th>Status</th>
                  <th>Kategori</th>
                  <th>Kabel</th>
                  <th className="is-num">Panjang</th>
                  <th className="is-num">Tiang</th>
                  <th>Wilayah</th>
                  <th className="is-end">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const target = subject('Route', { areaCode: r.areaCode });
                  return (
                    <tr
                      key={r.id}
                      aria-selected={selected.has(r.id) || (panel?.kind === 'view' && panel.id === r.id)}
                      onClick={() => setPanel({ kind: 'view', id: r.id })}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectable && (
                        <td className="is-check">
                          <input
                            type="checkbox"
                            aria-label="Pilih jalur"
                            checked={selected.has(r.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => toggleRow(r.id, e.target.checked)}
                          />
                        </td>
                      )}
                      <td>
                        <div className="fn-asset">
                          <AssetGlyph type={r.from.typeCode} />
                          <div>
                            <div className="fn-asset__name">{r.name ?? `${r.from.name} → ${r.to.name}`}</div>
                            <div className="fn-asset__code">
                              {r.from.code} → {r.to.code} · {ROUTE_MODE_LABELS[r.mode]}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge tone={ROUTE_STATUS_TONE[r.status] ?? 'neutral'} dot>
                          {ROUTE_STATUS_LABELS[r.status]}
                        </Badge>
                      </td>
                      <td>{ROUTE_CATEGORY_LABELS[r.category]}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {r.cableType ?? '—'}
                        {r.coreCount ? ` · ${r.coreCount} core` : ''}
                      </td>
                      <td className="is-num" style={{ whiteSpace: 'nowrap' }}>
                        {metres(r.lengthM)}
                      </td>
                      <td className="is-num">{nf.format(r.poles)}</td>
                      <td>{r.areaCode ? <RegionChip code={r.areaCode} /> : '—'}</td>
                      <td className="is-end">
                        <div className="fn-table__actions">
                          {trash ? (
                            ability.can('delete', target) && (
                              <Button
                                size="sm"
                                icon={<RotateCcw className="i" aria-hidden />}
                                onClick={stop(() => void restore(r))}
                              >
                                Pulihkan
                              </Button>
                            )
                          ) : (
                            <button
                              type="button"
                              className="fn-icon-btn fn-icon-btn--sm"
                              aria-label="Fokus di peta"
                              title="Fokus di peta"
                              onClick={stop(() => void navigate({ to: '/map', search: { route: r.id } }))}
                            >
                              <Crosshair className="i i--sm" aria-hidden />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="fn-table-footer">
          <div>
            <strong>{nf.format(data?.total ?? 0)}</strong> jalur · total{' '}
            <strong>{metres(data?.totalLengthM ?? 0)}</strong>
            {trash && data?.items[0]?.deletedAt && (
              <span className="fn-hint">
                {' '}
                · terakhir dihapus {dateTime.format(new Date(data.items[0].deletedAt))}
              </span>
            )}
          </div>
          <nav className="fn-pagination" aria-label="Halaman">
            <button
              type="button"
              className="fn-page"
              disabled={page <= 1}
              aria-label="Sebelumnya"
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="i i--sm" aria-hidden />
            </button>
            <span className="fn-page" aria-current="page">
              {page}
            </span>
            <span className="fn-hint">dari {lastPage}</span>
            <button
              type="button"
              className="fn-page"
              disabled={page >= lastPage}
              aria-label="Selanjutnya"
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="i i--sm" aria-hidden />
            </button>
          </nav>
        </div>
      </section>

      {panel?.kind === 'view' && (
        <RouteDrawer
          key={panel.id}
          id={panel.id}
          onClose={() => setPanel(null)}
          onEdit={(route) => setPanel({ kind: 'edit', route })}
          onOpenAsset={(id) => void navigate({ to: '/map', search: { focus: id } })}
          onFocus={(route) => void navigate({ to: '/map', search: { route: route.id } })}
          onDeleted={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'edit' && (
        <RouteForm
          key={`edit-${panel.route.id}`}
          mode="edit"
          route={panel.route}
          onClose={() => setPanel({ kind: 'view', id: panel.route.id })}
          onSaved={(r) => setPanel({ kind: 'view', id: r.id })}
        />
      )}
      {bulkOpen && (
        <ConfirmDialog
          title={`Hapus ${nf.format(selected.size)} jalur?`}
          icon={<Trash2 className="i" aria-hidden />}
          confirmLabel="Hapus"
          onClose={() => setBulkOpen(false)}
          onConfirm={async () => {
            const res = await send<{ deleted: number; skipped: Array<{ id: string; reason: string }> }>(
              'POST',
              '/api/v1/routes/bulk-delete',
              { ids: [...selected], withPoles },
            );
            await invalidateAssets(qc);
            setSelected(new Set(res.skipped.map((s) => s.id)));
            setBulkOpen(false);
            toast({
              tone: res.skipped.length ? 'warning' : 'success',
              text: `${nf.format(res.deleted)} jalur dipindah ke tempat sampah${res.skipped.length ? `; ${res.skipped.length} dilewati (${res.skipped[0]!.reason})` : ''}.`,
            });
          }}
        >
          <span>Jalur dipindah ke tempat sampah dan bisa dipulihkan dalam 30 hari.</span>
          <label className="fn-choice" style={{ cursor: 'pointer' }}>
            <span>
              <span className="fn-choice__title">Hapus juga tiang otomatisnya</span>
              <span className="fn-choice__desc">Hanya tiang yang dibuat otomatis dari jalur tersebut.</span>
            </span>
            <input
              type="checkbox"
              checked={withPoles}
              onChange={(e) => setWithPoles(e.target.checked)}
              style={{ width: 18, height: 18, flex: 'none' }}
            />
          </label>
        </ConfirmDialog>
      )}
    </div>
  );
}
